import React, { useEffect } from 'react';
import { arrayOf, bool, string } from 'prop-types';
import {
  useHelmet, TitleTag, MetaTag, ScriptTag, LinkTag
} from '@thd-nucleus/thd-helmet';
import { copy } from '@thd-olt-functional/utils';
import { useStoreId } from '@thd-nucleus/experience-context';
import {
  arrayOf as arrayType,
  bool as boolType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { getVideoStructuredData } from './structured-data/getVideoStructuredData';
import { getBreadcrumbStructuredData } from './structured-data/getBreadcrumbStructuredData';

import { getProductStructuredData } from './structured-data/getProductStructuredData';

function formatDescription(description) {
  if (!description) return '';
  const MAX_LEN = 199;
  if (description.length < MAX_LEN || description[MAX_LEN] === ' ') return description;
  let nextSpace = description.indexOf(' ', MAX_LEN);
  return description.substring(0, nextSpace);
}

function setBrandName(brandName) {
  if (brandName !== undefined) {
    if (brandName === 'Unbranded') {
      return '';
    }
  }
  return brandName;
}

const productMetaDataHandler = ({ data, props = {} }) => {
  if (!data?.product) {
    return [];
  }

  const {
    itemId,
    details,
    identifiers,
    seoDescription,
    seo,
    availabilityType,
    media,
    taxonomy,
    reviews,
    pricing,
    specificationGroup
  } = data?.product || {};

  let brand = identifiers?.brandName || '';

  let brandName = setBrandName(brand);

  // eslint-disable-next-line max-len
  const defaultTitle = `${brandName} ${identifiers?.productLabel || ''}-${identifiers?.modelNumber || ''} - The Home Depot`.trim();

  const title = props?.pageTitle?.trim() || defaultTitle;

  const defaultDescription = formatDescription(seoDescription || details?.description);
  const description = props?.pageDescription || defaultDescription;
  const seoKeywords = seo?.seoKeywords;
  const { canonicalUrl = '' } = identifiers || {};
  const identifierUrl = props?.reviewsCanonical ? canonicalUrl.replace('/p/', '/p/reviews/') : canonicalUrl;
  const pageUrl = props?.pageNumber ? identifierUrl.concat(`/${props.pageNumber}`) : identifierUrl;
  const canonical = `https://www.homedepot.com${pageUrl}`;
  const { video, image, images = [] } = media || {};
  const primaryImage = image?.url;
  const alternate = `android-app://com.thehomedepot/homedepot/product/${itemId}`;
  const regex = /_\d+./gm;
  const hoistedImgPath = primaryImage.replace(regex, '_600.');
  const secondaryImage = images.length > 1 ? images?.[1]?.url : '';
  const secondayImgPath = secondaryImage.replace(/_<SIZE>./g, '_600.');
  const tags = [
    new TitleTag(title),
    new MetaTag({ name: 'description', content: description, id: 'description' }),
    new MetaTag({ name: 'keywords', content: seoKeywords, id: 'keywords' }),
    new MetaTag({ property: 'og:description', content: description, id: 'ogDescription' }),
    new MetaTag({ property: 'og:title', content: title, id: 'ogTitle' }),
    new MetaTag({ property: 'og:url', content: canonical, id: 'ogUrl' }),
    new MetaTag({ property: 'og:image', content: primaryImage, id: 'ogImage' }),
    new MetaTag({ property: 'og:type', content: 'product', id: 'ogType' }),
    new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'ogSiteName' }),
    new MetaTag({ property: 'fb:admins', content: '100005416960152', id: 'fbAdmins' }),
    new LinkTag({ rel: 'canonical', href: canonical, id: 'canonical' }),
    new LinkTag({ rel: 'alternate', href: alternate, id: 'alternate' }),
    new LinkTag({
      rel: 'preload', href: hoistedImgPath, id: 'preloadImg', as: 'image', media: '(max-width: 600px)'
    }),
  ];
  if (secondayImgPath) {
    tags.push(new LinkTag({
      rel: 'preload', href: secondayImgPath, id: 'preloadSecondaryImg', as: 'image', media: '(max-width: 600px)'
    }));
  }
  const productStructure = getProductStructuredData({
    aggregateReviews: data.product?.reviews?.ratingsReviews,
    reviews: data?.reviews?.Results,
    identifiers,
    media,
    details,
    pricing,
    specifications: specificationGroup,
    availabilityType,
    schemaExclude: props.schema
  });

  tags.push(new ScriptTag({ content: productStructure, id: 'productStructureData' }));

  const breadcrumbStructuredData = getBreadcrumbStructuredData({
    canonical,
    breadcrumbs: copy(taxonomy?.breadCrumbs || []).data,
    title
  });

  tags.push(new ScriptTag({ content: breadcrumbStructuredData, id: 'breadcrumbStructureData' }));

  if (availabilityType?.discontinued) {
    tags.push(new MetaTag({ name: 'robots', content: 'noindex', id: 'robots' }));
  }
  if (video?.length) {
    const videoStructuredData = getVideoStructuredData({
      videos: video,
      productLabel: identifiers?.productLabel || '',
    });
    tags.push(new ScriptTag({ content: videoStructuredData, id: 'videoStructureData' }));
  }

  return tags;
};

const ProductMetadata = ({ itemId }) => {
  const storeId = useStoreId();
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-metadata.ready'); }, []);
  const productOptions = {
    variables: {
      itemId,
      storeId
    },
  };
  const reviewsOptions = {
    variables: {
      itemId
    },
  };
  const { data, loading, error, variables } = useDataModel('product', productOptions);

  const { data: reviewData, loading: reviewLoading, reviewError } = useDataModel('reviews', reviewsOptions);
  const props = {
    reviewsCanonical: false,
    pageNumber: null,
    pageTitle: null,
  };
  const helmetData = {
    product: data?.product,
    reviews: reviewData?.reviews
  };
  const deps = data?.product && reviewData?.reviews && data?.product?.itemId;
  useHelmet('productMetadata', { data: helmetData, props, variables }, productMetaDataHandler, [deps]);
  return null;
};

ProductMetadata.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    availabilityType: shapeType({
      discontinued: boolType(),
      status: boolType()
    }),
    details: shapeType({
      description: stringType()
    }),
    identifiers: shapeType({
      brandName: stringType(),
      canonicalUrl: stringType(),
      itemId: stringType(),
      modelNumber: stringType(),
      productLabel: stringType(),
      storeSkuNumber: stringType(),
      upcGtin13: stringType()
    }),
    media: shapeType({
      image: shapeType({
        url: stringType()
      }).client(),
      images: arrayType(shapeType({
        url: stringType(),
        type: stringType(),
        subType: stringType(),
        sizes: arrayType(stringType())
      })),
      video: arrayType(shapeType({
        shortDescription: stringType(),
        thumbnail: stringType(),
        url: stringType(),
        videoStill: stringType()
      }))
    }),
    pricing: params({ storeId: stringType() }).shape({
      promotion: shapeType({
        dates: shapeType({
          end: stringType()
        })
      }),
      value: numberType({ float: true })
    }),
    reviews: shapeType({
      ratingsReviews: shapeType({
        averageRating: stringType(),
        totalReviews: stringType()
      })
    }),
    seo: shapeType({
      seoKeywords: stringType(),
      seoDescription: stringType()
    }),
    specificationGroup: arrayType(shapeType({
      specifications: arrayType(shapeType({
        specName: stringType(),
        specValue: stringType()
      })),
      specTitle: stringType()
    })),
    taxonomy: shapeType({
      breadCrumbs: arrayType(shapeType({
        label: stringType(),
        url: stringType()
      }))
    })
  }),
  reviews: params({ itemId: stringType().isRequired() }).shape({
    Results: arrayType(shapeType({
      Rating: numberType(),
      ReviewText: stringType(),
      Title: stringType(),
      UserNickname: stringType()
    }))
  })
};

ProductMetadata.propTypes = {
  itemId: string.isRequired,
  reviewsCanonical: bool,
  pageNumber: string,
  pageTitle: string,
  pageDescription: string,
  schemaExclude: arrayOf(string)
};

ProductMetadata.defaultProps = {
  reviewsCanonical: false,
  pageNumber: null,
  pageTitle: null,
  pageDescription: null,
  schemaExclude: null
};

export { ProductMetadata };
